import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from '../../../../utils/GetCookie';
import authNavigate from "../../../../globalComponents/AuthNav";
import DocumentPreview from "../../../../utils/DocumentPreview";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import PaginationControls from "../../../../utils/PaginationControls";
import ConfirmationModal from "../../../../utils/ConfirmationModal";
import JSZip from "jszip";

/* ── Access filter options ── */
const ACCESS_FILTERS = [
    { id: "ALL",          label: "All" },
    { id: "OPEN_ACCESS",  label: "Open Access" },
    { id: "UNPURCHASED",  label: "Unpurchased" },
    { id: "ORDER_PLACED", label: "Order Placed" },
    { id: "PURCHASED",    label: "Purchased" },
];

/* ── Access badge — same pattern as ResultsList ── */
const renderAccessBadge = (status, { onPurchase, canPurchase, price } = {}) => {
    if (!status || status === "UNKNOWN") return <span className="discovery-badge-unknown text-center">Unknown</span>;
    if (status === "OPEN_ACCESS")
        return <span className="discovery-badge-green">Open Access <i className="bi bi-unlock-fill ms-1"></i></span>;
    if (status === "ORDER_PLACED")
        return <span className="discovery-badge-orange">Order Placed</span>;
    if (status === "PURCHASED")
        return <span className="discovery-badge-green">Purchased <i className="bi bi-check-circle-fill ms-1"></i></span>;
    if (status === "UNPURCHASED") {
        return (
            <div className="d-flex flex-column align-items-center gap-1">
                <span className="discovery-badge-orange">Unpurchased</span>
                {canPurchase && onPurchase && price != null && (
                    <button
                        className="ev-btn-blue-fill-round ev-btn-xsmall"
                        onClick={e => { e.stopPropagation(); onPurchase(); }}
                        title={`Purchase for $${Number(price).toFixed(2)}`}
                    >
                        
                        ${Number(price).toFixed(2)}
                    </button>
                )}
            </div>
        );
    }
    return <span className="discovery-badge-unknown text-center">Unknown</span>;
};

const FullTextList = ({ fabricId }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("included");

    /* ── Tab 1 — Included Papers ── */
    const [papers, setPapers]               = useState([]);
    const [paperMeta, setPaperMeta]         = useState({ total: 0, linked: 0, unlinked: 0 });
    const [selectedPaperIds, setSelectedPaperIds] = useState(new Set());
    const [p1Page, setP1Page]               = useState(1);
    const [p1Size, setP1Size]               = useState(25);
    const [p1TotalPages, setP1TotalPages]   = useState(1);
    const [p1Search, setP1Search]           = useState("");
    const [p1SearchQ, setP1SearchQ]         = useState("");
    // Bibliographic = papers added via Full-Text QC → Add Paper
    const [p1Bibliographic, setP1Bibliographic] = useState(false);
    const p1BibliographicRef = useRef(false);
    p1BibliographicRef.current = p1Bibliographic;
    const [bibliographicIds, setBibliographicIds] = useState(() => new Set());
    const [p1Loading, setP1Loading]         = useState(false);
    const [p1FtFilter, setP1FtFilter]         = useState("ALL");
    const [p1GenaiFilter, setP1GenaiFilter]   = useState("ALL");
    const [ftDropOpen, setFtDropOpen]         = useState(false);
    const [genaiDropOpen, setGenaiDropOpen]   = useState(false);

    /* ── Tab 2 — Full Text Papers ── */
    const [documents, setDocuments]         = useState([]);
    const [counts, setCounts]               = useState({ total: 0 });
    const [selectedDocIds, setSelectedDocIds] = useState(new Set());
    const [p2Page, setP2Page]               = useState(1);
    const [p2Size, setP2Size]               = useState(25);
    const [p2TotalPages, setP2TotalPages]   = useState(1);
    const [p2Search, setP2Search]           = useState("");
    const [p2SearchQ, setP2SearchQ]         = useState("");
    const [p2Loading, setP2Loading]         = useState(false);

    /* ── Migrate state ── */
    const [isMigrating, setIsMigrating]                   = useState(false);
    const [migrationStatus, setMigrationStatus] = useState(null); // null | "RUNNING" | "COMPLETED" | "FAILED" | "IN_PROGRESS"
    const [pendingMigrateCount, setPendingMigrateCount]   = useState(0);

    /* ── Discovery-level fulltext_migration_status, keyed by discovery_id ── */
    const [sourceSearchStatusById, setSourceSearchStatusById] = useState({});

    /* ── Discovery-level fulltext_status (fetch process), keyed by discovery_id ── */
    const [fetchStatusById, setFetchStatusById] = useState({});

    /* ── Local fetch status (set when fetch starts) ── */
    const [localFetchStatus, setLocalFetchStatus] = useState(null);

    /* ── Discovery-level genai_status, keyed by discovery_id ── */
    const [genaiStatusById, setGenaiStatusById] = useState({});

    /* ── Local GenAI rights status ── */
    const [localGenaiStatus, setLocalGenaiStatus] = useState(null);

    // Derive migration status directly from backend-reported fulltext_migration_status
    // values in sourceSearchStatusById — show exactly what the backend says.
    // Priority: FAILED > RUNNING > partial COMPLETED (IN_PROGRESS) > all COMPLETED
    const derivedMigrationStatus = useMemo(() => {
        const statuses = Object.values(sourceSearchStatusById).filter(Boolean);
        if (!statuses.length) return null;
        if (statuses.some(s => s === "FAILED"))     return "FAILED";
        if (statuses.some(s => s === "RUNNING"))    return "RUNNING";
        if (statuses.every(s => s === "COMPLETED")) return "COMPLETED";
        if (statuses.some(s => s === "COMPLETED"))  return "IN_PROGRESS";
        return null;
    }, [sourceSearchStatusById]);

    // Local state takes priority (RUNNING/FAILED during active operation), fallback to derived
    const displayMigrationStatus = migrationStatus || derivedMigrationStatus;

    // Derive fetch status directly from backend fulltext_status values
    const derivedFetchStatus = useMemo(() => {
        const statuses = Object.values(fetchStatusById).filter(Boolean);
        if (!statuses.length) return null;
        if (statuses.some(s => s === "FAILED"))     return "FAILED";
        if (statuses.some(s => s === "RUNNING"))    return "RUNNING";
        if (statuses.every(s => s === "COMPLETED")) return "COMPLETED";
        if (statuses.some(s => s === "COMPLETED"))  return "IN_PROGRESS";
        return null;
    }, [fetchStatusById]);

    // Local state (set when fetch starts) takes priority, fallback to derived
    const displayFetchStatus = localFetchStatus || derivedFetchStatus;

    // GenAI rights status — derived from backend genai_status values,

    const derivedGenaiStatus = useMemo(() => {
        const statuses = Object.values(genaiStatusById).filter(Boolean);
        if (!statuses.length) return null;
        if (statuses.some(s => s === "FAILED"))     return "FAILED";
        if (statuses.some(s => s === "RUNNING"))    return "RUNNING";
        if (statuses.every(s => s === "COMPLETED")) return "COMPLETED";
        if (statuses.some(s => s === "COMPLETED"))  return "IN_PROGRESS";
        return null;
    }, [genaiStatusById]);

    const displayGenaiStatus = localGenaiStatus || derivedGenaiStatus;
    const [isFetchingFT, setIsFetchingFT]       = useState(false);
    const [isFetchingGR, setIsFetchingGR]       = useState(false);


    /* ── Upload ── */
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);

    /* ── Filter dropdown refs (outside-click) ── */
    const ftFilterRef = useRef(null);
    const genaiFilterRef = useRef(null);

    useEffect(() => {
        if (!ftDropOpen && !genaiDropOpen) return;
        const handleClickOutside = (e) => {
            if (
                ftFilterRef.current && !ftFilterRef.current.contains(e.target) &&
                genaiFilterRef.current && !genaiFilterRef.current.contains(e.target)
            ) {
                setFtDropOpen(false);
                setGenaiDropOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [ftDropOpen, genaiDropOpen]);

    /* ── Preview ── */
    const [previewOpen, setPreviewOpen]     = useState(false);
    const [previewDocId, setPreviewDocId]   = useState(null);
    const [previewDocName, setPreviewDocName] = useState("");

    /* ── Purchase modals ── */
    const [purchaseModal, setPurchaseModal] = useState({
        open: false, paper: null, orderType: "PDF",
    });
    const [bulkPurchaseModal, setBulkPurchaseModal] = useState({
        open: false, papers: [], orderType: "PDF",
    });
    const [purchaseConfirmText, setPurchaseConfirmText]         = useState("");
    const [bulkPurchaseConfirmText, setBulkPurchaseConfirmText] = useState("");

    /* ── Popups ── */
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage]               = useState("");
    const [isSuccessPopupVisible, setIsSuccessPopupVisible]       = useState(false);
    const [popupSuccessMessage, setPopupSuccessMessage]           = useState("");
    const [isErrorPopupVisible, setIsErrorPopupVisible]           = useState(false);
    const [popupErrorMessage, setPopupErrorMessage]               = useState("");

    const PHASE_LINK_URL = "/pceapi/slr/phase-linking";
    const FT_URL         = "/pceapi/slr";
    const DISCOVERY_URL  = "/pceapi/slr/discovery";
    const headers = useMemo(() => ({ Authorization: GetCookie('access_token') }), []);

    /* ── Resolve slr_fabric_config.id from fabricId (data_fabric.id) ── */
    const [configFabricId, setConfigFabricId] = useState(null);
    const [projectName, setProjectName] = useState("full_text_papers");
    useEffect(() => {
        if (!fabricId) return;
        axios.post(`${FT_URL}/form/get-project-details`, { slr_fabric_id: fabricId }, { headers })
            .then(res => {
                if (res.data?.status === "success") {
                    const cfgId = res.data?.data?.config_id ?? fabricId;
                    const name  = res.data?.data?.stages?.general_settings?.data?.name;
                    setConfigFabricId(cfgId);
                    if (name) setProjectName(name.trim().replace(/\s+/g, "_"));
                } else {
                    setConfigFabricId(fabricId);
                }
            })
            .catch(() => setConfigFabricId(fabricId));
    }, [fabricId]); // eslint-disable-line

    /* ─────────────── TAB 1 — fetch included papers ─────────────── */
    const fetchIncluded = useCallback(async (pg, sz, search, cfgId, { silent = false } = {}) => {
        if (!cfgId) return;
        if (!silent) {
            setP1Loading(true);
            setIsProcessingPopupVisible(true);
            setProcessingMessage("Loading included papers…");
        }
        try {
            const res = await axios.post(`${PHASE_LINK_URL}/list`, {
                slr_fabric_id: cfgId,
                page: pg, size: sz,
                category: p1BibliographicRef.current ? "BIBLIOGRAPHIC" : "ALL",
                search: search || null,
                sort_order: "asc",
            }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status === "success") {
                const inner = res.data?.data || {};
                const docs  = Array.isArray(inner?.documents) ? inner.documents : [];
                const pag   = inner?.pagination || {};
                const cnts  = inner?.counts || {};
                setPapers(docs);
                setPaperMeta({ total: cnts.total ?? 0, linked: cnts.linked ?? 0, unlinked: cnts.unlinked ?? 0 });
                setP1TotalPages(pag.total_pages ?? (Math.ceil((cnts.total ?? 0) / sz) || 1));

                // Index discovery-level fulltext_migration_status by discovery_id.
                // Only overwrite when the API actually returns source_searches — an
                // empty array on a subsequent call (e.g. Refresh) must not wipe out
                // a previously populated map, which would clear the migration badge.
                const searches = inner?.metadata?.source_searches || [];
                // Uploaded (non-discovery) studies run through the SLR cascade,
                // which reports its own project-level status.
                const slrFetchStatus = inner?.metadata?.slr_fulltext_status || null;
                if (searches.length > 0 || slrFetchStatus) {
                    const statusMap  = {};
                    const fetchMap   = {};
                    const genaiMap   = {};
                    searches.forEach(s => {
                        statusMap[s.discovery_id] = s.fulltext_migration_status;
                        fetchMap[s.discovery_id]  = s.fulltext_status;
                        genaiMap[s.discovery_id]  = s.genai_status;
                    });
                    if (slrFetchStatus) fetchMap.__slr__ = slrFetchStatus;
                    if (searches.length > 0) setSourceSearchStatusById(statusMap);
                    setFetchStatusById(fetchMap);
                    setGenaiStatusById(genaiMap);

                    // Clear stale local status if backend no longer reports RUNNING
                    const fetchStatuses = Object.values(fetchMap).filter(Boolean);
                    if (!fetchStatuses.some(s => s === "RUNNING")) setLocalFetchStatus(null);
                    const genaiStatuses = Object.values(genaiMap).filter(Boolean);
                    if (!genaiStatuses.some(s => s === "RUNNING")) setLocalGenaiStatus(null);
                } else {
                    // No source searches — clear any stale running badges
                    setLocalFetchStatus(null);
                    setLocalGenaiStatus(null);
                }
            } else if (!silent) {
                setPopupErrorMessage(res.data?.message || "Failed to fetch included papers");
                setIsErrorPopupVisible(true);
            }
        } catch (err) {
            if (!silent) {
                setPopupErrorMessage(err.response?.data?.message || "Failed to fetch included papers");
                setIsErrorPopupVisible(true);
            }
        } finally {
            if (!silent) { setP1Loading(false); setIsProcessingPopupVisible(false); }
        }
    }, [fabricId, headers, navigate]);

    useEffect(() => {
        if (configFabricId && activeTab === "included") fetchIncluded(p1Page, p1Size, p1SearchQ, configFabricId);
    }, [configFabricId, activeTab, p1Page, p1Size, p1SearchQ, p1Bibliographic]); // eslint-disable-line

    // "Bibliographic" badge: study_ids of papers added via Add Paper
    useEffect(() => {
        if (!fabricId) return;
        let cancelled = false;
        axios.post(`${FT_URL}/qc/bibliographic-study-ids`, { slr_fabric_config_id: fabricId }, { headers })
            .then((res) => {
                if (!cancelled && res.data?.status === "success") {
                    setBibliographicIds(new Set((res.data.study_ids || []).map(String)));
                }
            })
            .catch((err) => console.error("Bibliographic ids fetch failed:", err));
        return () => { cancelled = true; };
    }, [fabricId]); // eslint-disable-line

    // Filter is hidden when there are no bibliographic papers — never leave it stuck ON
    useEffect(() => {
        if (bibliographicIds.size === 0 && p1Bibliographic) setP1Bibliographic(false);
    }, [bibliographicIds, p1Bibliographic]);

    /* ─────────────── TAB 2 — fetch full-text docs ─────────────── */
    const fetchDocuments = useCallback(async (pg, sz, search) => {
        if (!fabricId) return;
        setP2Loading(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Fetching full-text documents...");
        try {
            const res = await axios.post(`${FT_URL}/full-text-screening/list`, {
                fabric_id: fabricId, page: pg, size: sz, search: search || null,
            }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status === "success") {
                const docs = res.data.data || [];
                const pag  = res.data.pagination || {};
                const total = pag.total ?? docs.length;
                setDocuments(docs);
                setP2TotalPages(pag.total_pages ?? (Math.ceil(total / sz) || 1));
                setCounts({ total });
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to fetch documents");
                setIsErrorPopupVisible(true);
            }
        } catch (err) {
            setPopupErrorMessage(err.response?.data?.message || "Failed to fetch documents");
            setIsErrorPopupVisible(true);
        } finally { setP2Loading(false); setIsProcessingPopupVisible(false); }
    }, [fabricId, headers, navigate]);

    useEffect(() => {
        if (fabricId && activeTab === "fulltext") fetchDocuments(p2Page, p2Size, p2SearchQ);
    }, [fabricId, activeTab, p2Page, p2Size, p2SearchQ]);

    const handleFetchFullText = async () => {
        if (!configFabricId) return;
        const ids = [...selectedPaperIds];
        setIsFetchingFT(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage(
            ids.length ? `Searching full text for ${ids.length} papers…`
                       : "Searching full text for all included papers…"
        );
        try {
            const res = await axios.post(`${PHASE_LINK_URL}/fetch-full-text`, {
                slr_fabric_id: configFabricId,
                study_ids: ids.length ? ids : null,
            }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status === "success") {
                setLocalFetchStatus("RUNNING");
                setPopupSuccessMessage(res.data.message || "Full text search started.");
                setIsSuccessPopupVisible(true);
                setSelectedPaperIds(new Set());
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to start full text search.");
                setIsErrorPopupVisible(true);
            }
        } catch (err) {
            setPopupErrorMessage(err.response?.data?.message || "Failed to start full text search.");
            setIsErrorPopupVisible(true);
        } finally {
            setIsFetchingFT(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* ─────────────── Fetch GenAI Rights ─────────────── */
    const handleFetchGenAIRights = async () => {
        if (!configFabricId) return;
        const ids = [...selectedPaperIds];
        setIsFetchingGR(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Checking GenAI rights…");
        try {
            const res = await axios.post(`${PHASE_LINK_URL}/fetch-genai-rights`, {
                slr_fabric_id: configFabricId,
                study_ids: ids.length ? ids : null,
            }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status === "success") {
                setLocalGenaiStatus("RUNNING");
                setPopupSuccessMessage(res.data.message || "GenAI rights check started.");
                setIsSuccessPopupVisible(true);
                setSelectedPaperIds(new Set());
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to start GenAI rights check.");
                setIsErrorPopupVisible(true);
            }
        } catch (err) {
            setPopupErrorMessage(err.response?.data?.message || "Failed to start GenAI rights check.");
            setIsErrorPopupVisible(true);
        } finally {
            setIsFetchingGR(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* ─────────────── Purchase — single paper ─────────────── */
    const handlePurchase = (paper, orderType = "PDF") => {
        setPurchaseConfirmText("");
        setPurchaseModal({ open: true, paper, orderType });
    };

    const handlePurchaseConfirm = async () => {
        if (purchaseConfirmText.trim() !== "purchase") {
            setPopupErrorMessage("Please type 'purchase' to confirm.");
            setIsErrorPopupVisible(true);
            return;
        }
        const { paper, orderType } = purchaseModal;
        setPurchaseModal({ open: false, paper: null, orderType: "PDF" });
        setPurchaseConfirmText("");
        setIsProcessingPopupVisible(true);
        setProcessingMessage(orderType === "GENAI" ? "Placing GenAI rights order…" : "Placing Galaxy order…");
        try {
            const res = await axios.post(`${DISCOVERY_URL}/galaxy-order`, {
                discovery_id: paper.discovery_id,
                paper_id: paper.discovery_result_id,
                order_type: orderType,
            }, { headers });
            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status === "success") {
                setPapers(prev => prev.map(p =>
                    p.phase1_id === paper.phase1_id
                        ? orderType === "GENAI"
                            ? { ...p, genai_status: "ORDER_PLACED" }
                            : { ...p, full_text_status: "ORDER_PLACED" }
                        : p
                ));
                setPopupSuccessMessage(
                    orderType === "GENAI" ? "GenAI rights order placed successfully." : "Order placed successfully."
                );
                setIsSuccessPopupVisible(true);
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to place order.");
                setIsErrorPopupVisible(true);
            }
        } catch (err) {
            setPopupErrorMessage(err.response?.data?.message || "Error placing order.");
            setIsErrorPopupVisible(true);
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* ─────────────── Purchase — bulk ─────────────── */
    /* Purchasable = UNPURCHASED + is_discovery (has discovery_result_id) */
    const getPurchasablePapers = (orderType = "PDF") => {
        const statusKey = orderType === "GENAI" ? "genai_status" : "full_text_status";
        return papers.filter(p =>
            selectedPaperIds.has(p.study_id ?? p.phase1_id) &&
            p.is_discovery &&
            p.discovery_result_id &&
            p[statusKey] === "UNPURCHASED"
        );
    };

    const handleBulkPurchase = (orderType = "PDF") => {
        const purchasable = getPurchasablePapers(orderType);
        if (!purchasable.length) {
            setPopupErrorMessage("No selected papers are eligible for purchase (must be Unpurchased and from Discovery).");
            setIsErrorPopupVisible(true);
            return;
        }
        setBulkPurchaseConfirmText("");
        setBulkPurchaseModal({ open: true, papers: purchasable, orderType });
    };

    const handleBulkPurchaseConfirm = async () => {
        if (bulkPurchaseConfirmText.trim() !== "purchase") {
            setPopupErrorMessage("Please type 'purchase' to confirm.");
            setIsErrorPopupVisible(true);
            return;
        }
        const { papers: toBuy, orderType } = bulkPurchaseModal;
        setBulkPurchaseModal({ open: false, papers: [], orderType: "PDF" });
        setBulkPurchaseConfirmText("");
        setIsProcessingPopupVisible(true);
        let successCount = 0;
        let failCount = 0;
        for (const paper of toBuy) {
            setProcessingMessage(`Ordering ${successCount + failCount + 1} of ${toBuy.length}…`);
            try {
                const res = await axios.post(`${DISCOVERY_URL}/galaxy-order`, {
                    discovery_id: paper.discovery_id,
                    paper_id: paper.discovery_result_id,
                    order_type: orderType,
                }, { headers });
                if (res.data?.status === "success") {
                    setPapers(prev => prev.map(p =>
                        p.phase1_id === paper.phase1_id
                            ? orderType === "GENAI"
                                ? { ...p, genai_status: "ORDER_PLACED" }
                                : { ...p, full_text_status: "ORDER_PLACED" }
                            : p
                    ));
                    successCount++;
                } else {
                    failCount++;
                }
            } catch {
                failCount++;
            }
        }
        setIsProcessingPopupVisible(false);
        setProcessingMessage("");
        if (successCount > 0) {
            setPopupSuccessMessage(`${successCount} order(s) placed successfully.${failCount > 0 ? ` ${failCount} failed.` : ""}`);
            setIsSuccessPopupVisible(true);
            setSelectedPaperIds(new Set());
        } else {
            setPopupErrorMessage("All orders failed. Please try again.");
            setIsErrorPopupVisible(true);
        }
    };

    /* ─────────────── Migrate ─────────────── */
    const handleMigrate = async ({ project, phase }) => {
        if (!project?.id) {
            setPopupErrorMessage("Please select an SLR Project to migrate to.");
            setIsErrorPopupVisible(true); return;
        }
        if (selectedPaperIds.size === 0) {
            setPopupErrorMessage("Please select at least one paper to migrate.");
            setIsErrorPopupVisible(true); return;
        }
        const ids = [...selectedPaperIds];
        const selected = papers.filter(p => ids.includes(p.study_id ?? p.phase1_id));

        // Only papers that actually have a PDF can be migrated. Unpurchased /
        // unknown ones have no file yet, and anything already migrated would
        // just be dropped by the thread's content-hash dedupe.
        const migratable = selected.filter(p =>
            ["OPEN_ACCESS", "PURCHASED"].includes(p.full_text_status)
            && p.full_text_link
            && !p.migrated
            && !p.doc_id_linked
        );
        const skipped = selected.length - migratable.length;

        if (!migratable.length) {
            setPopupErrorMessage(
                selected.length
                    ? "None of the selected papers can be migrated — they either have no full text yet or are already in the project."
                    : "Select at least one paper to migrate."
            );
            setIsErrorPopupVisible(true);
            return;
        }

        // Discovery-origin papers migrate through Discovery, keyed by their
        // discovery_results_metadata id and grouped by their own search.
        const bySearch = {};
        migratable.filter(p => p.is_discovery && p.discovery_result_id)
                  .forEach(p => { (bySearch[p.discovery_id] ||= []).push(p.discovery_result_id); });
        // Everything else came from a manual upload and uses the SLR pipeline.
        const slrIds = migratable.filter(p => !p.is_discovery)
                                 .map(p => p.study_id ?? p.phase1_id);

        setPendingMigrateCount(migratable.length);
        setIsMigrating(true);
        setMigrationStatus("RUNNING");
        setIsProcessingPopupVisible(true);
        setProcessingMessage(`Migrating ${migratable.length} papers`);
        try {
            const calls = [];
            Object.entries(bySearch).forEach(([searchId, resultIds]) => {
                calls.push(axios.post(`${DISCOVERY_URL}/migrate`, {
                    fabric_id: project.id,
                    discovery_result_ids: resultIds,
                    migration_type: "full_text",
                    search_id: Number(searchId),
                }, { headers }));
            });
            if (slrIds.length) {
                calls.push(axios.post(`${PHASE_LINK_URL}/migrate-full-text`, {
                    slr_fabric_id: configFabricId,
                    study_ids: slrIds,
                }, { headers }));
            }

            const results = await Promise.all(calls);
            if (results.some(r => r.data?.status === "NotAuthorized")) {
                authNavigate('/login', 'iam', navigate); return;
            }
            const failed = results.filter(r => r.data?.status !== "success");
            if (failed.length) {
                setMigrationStatus("FAILED");
                setPopupErrorMessage(failed[0].data?.message || "Migration partially failed.");
                setIsErrorPopupVisible(true);
            } else {
                // Keep "RUNNING" — RAG pipeline still in progress.
                // checkMigrationDone poll will flip to "COMPLETED" once files land in thread.
                setPopupSuccessMessage(
                    `Migration queued for ${migratable.length} papers`
                    + (skipped ? ` — ${skipped} skipped (no full text yet, or already migrated)` : "")
                );
                setIsSuccessPopupVisible(true);
                setSelectedPaperIds(new Set());
            }
        } catch (err) {
            setMigrationStatus("FAILED");
            setPopupErrorMessage(err.response?.data?.message || "Failed to migrate papers.");
            setIsErrorPopupVisible(true);
        } finally { setIsMigrating(false); setIsProcessingPopupVisible(false); setProcessingMessage(""); }
    };

    /* ─────────────── Migration completion poller ─────────────── */
    // Silently checks whether the migration consumer has finished uploading PDFs
    // to the unstructured thread. Once the thread document count ≥ what we migrated,
    // the consumer is done and we can flip the badge to "COMPLETED".
    const checkMigrationDone = useCallback(async () => {
        if (!fabricId || !pendingMigrateCount) return;
        try {
            const res = await axios.post(
                `${FT_URL}/full-text-screening/list`,
                { fabric_id: fabricId, page: 1, size: 1 },
                { headers }
            );
            if (res.data?.status === "success") {
                const total = res.data?.pagination?.total ?? 0;
                if (total >= pendingMigrateCount) {
                    setMigrationStatus("COMPLETED");
                    setPendingMigrateCount(0);
                }
            }
        } catch { /* silent — don't surface polling errors to the user */ }
    }, [fabricId, pendingMigrateCount, headers]);

    useEffect(() => {
        if (migrationStatus !== "RUNNING" || !pendingMigrateCount) return;
        const id = setInterval(checkMigrationDone, 30_000);
        return () => clearInterval(id);
    }, [migrationStatus, pendingMigrateCount, checkMigrationDone]);

    /* ─────────────── Download documents ─────────────── */
    const downloadDoc = async (doc) => {
        try {
            const res = await axios.post(`${FT_URL}/full-text-screening/preview`, {
                fabric_id: fabricId,
                doc_id: doc.doc_id,
            }, { headers });
            const url = res.data?.presigned_url || res.data?.data?.presigned_url;
            if (!url) { console.error('No presigned_url for', doc.document_name, res.data); return null; }
            const blob = await fetch(url).then(r => r.blob());
            return { blob, name: doc.document_name || `document_${doc.doc_id}.pdf` };
        } catch (err) {
            console.error('Download failed for', doc.document_name, err);
            return null;
        }
    };

    const handleDownloadSelected = async () => {
        const toDownload = documents.filter(d => selectedDocIds.has(d.doc_id));
        if (!toDownload.length) return;
        setIsProcessingPopupVisible(true);
        setProcessingMessage(`Preparing zip of ${toDownload.length} file(s)…`);
        try {
            const zip = new JSZip();
            for (const doc of toDownload) {
                setProcessingMessage(`Fetching ${doc.document_name}…`);
                const result = await downloadDoc(doc);
                if (result) zip.file(result.name, result.blob);
            }
            setProcessingMessage("Creating zip file…");
            const zipBlob = await zip.generateAsync({ type: "blob" });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(zipBlob);
            a.download = `${projectName}.zip`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(a.href);
        } catch (err) {
            setPopupErrorMessage("Failed to create zip file.");
            setIsErrorPopupVisible(true);
        } finally { setIsProcessingPopupVisible(false); }
    };

    const handleDownloadAll = async () => {
        if (!documents.length) return;
        setIsProcessingPopupVisible(true);
        setProcessingMessage(`Preparing zip of ${documents.length} file(s)…`);
        try {
            const zip = new JSZip();
            for (const doc of documents) {
                setProcessingMessage(`Fetching ${doc.document_name}…`);
                const result = await downloadDoc(doc);
                if (result) zip.file(result.name, result.blob);
            }
            setProcessingMessage("Creating zip file…");
            const zipBlob = await zip.generateAsync({ type: "blob" });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(zipBlob);
            a.download = `${projectName}_full_texts.zip`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(a.href);
        } catch (err) {
            setPopupErrorMessage("Failed to create zip file.");
            setIsErrorPopupVisible(true);
        } finally { setIsProcessingPopupVisible(false); }
    };
    const handleFileChange = async (files) => {
        if (!files || files.length === 0) return;
        const fileArr = Array.from(files);
        setIsUploading(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage(`Uploading ${fileArr.length} file(s)…`);
        try {
            for (const file of fileArr) {
                const formData = new FormData();
                formData.append("pdf_file", file);
                formData.append("slr_fabric_config_id", configFabricId || fabricId);
                await axios.post(`${PHASE_LINK_URL}/upload-single-pdf`, formData, { headers });
            }
            setPopupSuccessMessage(`${fileArr.length} file(s) uploaded successfully`);
            setIsSuccessPopupVisible(true);
            if (activeTab === "fulltext") fetchDocuments(p2Page, p2Size, p2SearchQ);
        } catch (err) {
            setPopupErrorMessage(err.response?.data?.message || "Upload failed");
            setIsErrorPopupVisible(true);
        } finally {
            setIsUploading(false);
            setIsProcessingPopupVisible(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    /* ─────────────── Download list (Full-Text + GenAI status) ─────────────── */
    const handleDownloadList = async () => {
        if (!configFabricId) return;
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Preparing list…");
        try {
            // Pull the full unpaginated set — `papers` only holds the current
            // page, and an export that silently dropped other pages would be
            // worse than no export at all.
            const res = await axios.post(`${PHASE_LINK_URL}/list`, {
                slr_fabric_id: configFabricId,
                page: 1,
                size: Math.max(paperMeta.total || 0, 1),
                category: "ALL",
                search: null,
                sort_order: "asc",
            }, { headers });

            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status !== "success") {
                setPopupErrorMessage(res.data?.message || "Failed to prepare list");
                setIsErrorPopupVisible(true);
                return;
            }

            let exportRows = res.data?.data?.documents || [];

            // Selection still narrows the export if the user explicitly
            // checked specific papers. Otherwise export every included
            // paper — the Full-Text/GenAI dropdown filters (p1FtFilter/
            // p1GenaiFilter) are a view-only concern for the cards list and
            // must not silently drop rows from the download.
            if (selectedPaperIds.size > 0) {
                exportRows = exportRows.filter(p => selectedPaperIds.has(p.study_id ?? p.phase1_id));
            }

            if (!exportRows.length) {
                setPopupErrorMessage("No papers to export.");
                setIsErrorPopupVisible(true);
                return;
            }

            const escapeCsv = (val) => {
                const s = val == null ? "" : String(val);
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };

            const header = [
                "Study ID", "Title", "Authors", "Year",
                "Full-Text Status", "Full-Text Price",
                "GenAI Rights Status", "GenAI Price",
            ];
            const rows = exportRows.map(p => [
                p.id ?? p.study_id ?? "",
                p.title ?? "",
                p.authors ?? "",
                p.year ?? "",
                p.full_text_status || "UNKNOWN",
                p.delivery_price != null ? Number(p.delivery_price).toFixed(2) : "",
                p.genai_status || "UNKNOWN",
                p.genai_price != null ? Number(p.genai_price).toFixed(2) : "",
            ]);

            const csvContent = [header, ...rows]
                .map(r => r.map(escapeCsv).join(","))
                .join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${projectName}_full_text_genai_rights.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            setPopupErrorMessage(err.response?.data?.message || "Failed to download list");
            setIsErrorPopupVisible(true);
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* ─────────────── Tab 1 selection ─────────────── */
    const filteredPapers = useMemo(() => {
        return [...papers.filter(p => {
            const ftOk  = p1FtFilter    === "ALL" || p.full_text_status === p1FtFilter;
            const grOk  = p1GenaiFilter === "ALL" || p.genai_status     === p1GenaiFilter;
            return ftOk && grOk;
        })].sort((a, b) => (a.id ?? a.study_id ?? 0) - (b.id ?? b.study_id ?? 0));
    }, [papers, p1FtFilter, p1GenaiFilter]);

    const pageIds = filteredPapers.map(p => p.study_id ?? p.phase1_id);
    const isAllP1Selected = pageIds.length > 0 && pageIds.every(id => selectedPaperIds.has(id));
    const toggleAllP1 = () => isAllP1Selected
        ? setSelectedPaperIds(new Set())
        : setSelectedPaperIds(new Set(pageIds));
    const togglePaper = (id) => setSelectedPaperIds(prev => {
        const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
    });

    /* Bulk purchase eligibility across selected papers */
    const bulkPdfEligible  = useMemo(() => getPurchasablePapers("PDF").length,    [papers, selectedPaperIds]); // eslint-disable-line
    const bulkGenaiEligible = useMemo(() => getPurchasablePapers("GENAI").length, [papers, selectedPaperIds]); // eslint-disable-line

    /* Bulk purchase total price */
    const bulkPdfTotal   = useMemo(() =>
        getPurchasablePapers("PDF").reduce((sum, p) => sum + (p.delivery_price ?? 0), 0), // eslint-disable-line
    [papers, selectedPaperIds]); // eslint-disable-line
    const bulkGenaiTotal = useMemo(() =>
        getPurchasablePapers("GENAI").reduce((sum, p) => sum + (p.genai_price ?? 0), 0), // eslint-disable-line
    [papers, selectedPaperIds]); // eslint-disable-line

    /* ─────────────── Tab 2 selection ─────────────── */
    const isAllP2Selected = documents.length > 0 && documents.every(d => selectedDocIds.has(d.doc_id));
    const toggleAllP2 = () => isAllP2Selected ? setSelectedDocIds(new Set()) : setSelectedDocIds(new Set(documents.map(d => d.doc_id)));
    const toggleDoc = (id) => setSelectedDocIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

    const openPreview = (doc) => { setPreviewDocId(doc.doc_id); setPreviewDocName(doc.document_name); setPreviewOpen(true); };

    /* ─────────────── Purchase modal labels ─────────────── */
    const singlePurchasePrice = purchaseModal.orderType === "GENAI"
        ? purchaseModal.paper?.genai_price
        : purchaseModal.paper?.delivery_price;

    const bulkPurchaseTotal = bulkPurchaseModal.orderType === "GENAI" ? bulkGenaiTotal : bulkPdfTotal;

    /* ═══════════════════════════════════════════════════════════
       RENDER
    ═══════════════════════════════════════════════════════════ */
    return (
        <div className="h-100 d-flex flex-column" style={{ overflowX: "auto" }}>

            {/* ── TOP BAR: pill tabs + action buttons ── */}
            <div className="d-flex justify-content-between align-items-center mb-3" style={{ overflow: "visible" }}>

                {/* Pill tabs */}
                <div className="d-flex ev-card-2 p-1 ev-radius-large gap-1">
                    <button
                        className={activeTab === "included"
                            ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med px-3"
                            : "ev-btn-white-fill-round ev-btn-reg ev-bold-med px-3"}
                        onClick={() => setActiveTab("included")}
                    >
                        <i className="bi bi-journal-text me-1"></i>Included Papers
                    </button>
                    <button
                        className={activeTab === "fulltext"
                            ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med px-3"
                            : "ev-btn-white-fill-round ev-btn-reg ev-bold-med px-3"}
                        onClick={() => setActiveTab("fulltext")}
                    >
                        <i className="bi bi-file-earmark-pdf me-1"></i>Full Text Papers
                    </button>
                </div>

                {/* Action buttons */}
                {activeTab === "included" && (
                <div className="d-flex align-items-center gap-2 flex-shrink-0" style={{ overflow: "visible" }}>

                    {/* Migrate — direct, no project picker needed (already inside project) */}
                    <div className="d-flex align-items-center btn-group custom-btn-group-outer">
                        {displayMigrationStatus === "RUNNING" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-yellow-fill-round ev-btn-small ev-radius-large">
                                Running
                            </span>
                        )}
                        {displayMigrationStatus === "COMPLETED" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-blue-fill-round ev-btn-small ev-radius-large">
                                Completed
                            </span>
                        )}
                        {displayMigrationStatus === "IN_PROGRESS" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-yellow-fill-round ev-btn-small ev-radius-large">
                                In Progress
                            </span>
                        )}
                        {displayMigrationStatus === "FAILED" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-red-fill-round ev-btn-small ev-radius-large">
                                Failed
                            </span>
                        )}
                        <button
                            className="btn genbi-custom-btn-group-inner ev-btn-gradient-fill-round ev-btn-small ev-bold-med"
                            onClick={() => handleMigrate({ project: { id: fabricId }, phase: { id: "FULL_TEXT" } })}
                            disabled={selectedPaperIds.size === 0 || isMigrating}
                            title={selectedPaperIds.size > 0 ? `Migrate ${selectedPaperIds.size} selected papers` : "Select papers to migrate"}
                        >
                            {isMigrating
                                ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Migrating…</>
                                : <><i className="bi bi-arrow-left-right me-1"></i>Migrate</>
                            }
                        </button>
                    </div>

                    {/* Fetch Full Text — split-pill with fetch status badge */}
                    <div className="d-flex align-items-center btn-group custom-btn-group-outer">
                        {displayFetchStatus === "RUNNING" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-yellow-fill-round ev-btn-small ev-radius-large">
                                Running
                            </span>
                        )}
                        {displayFetchStatus === "COMPLETED" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-blue-fill-round ev-btn-small ev-radius-large">
                                Completed
                            </span>
                        )}
                        {displayFetchStatus === "IN_PROGRESS" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-yellow-fill-round ev-btn-small ev-radius-large">
                                In Progress
                            </span>
                        )}
                        {displayFetchStatus === "FAILED" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-red-fill-round ev-btn-small ev-radius-large">
                                Failed
                            </span>
                        )}
                        <button
                            className="btn genbi-custom-btn-group-inner ev-btn-lightblue-fill-round ev-btn-small"
                            onClick={handleFetchFullText}
                            disabled={isFetchingFT || !configFabricId}
                            title={selectedPaperIds.size
                                ? `Fetch full text for ${selectedPaperIds.size} selected papers`
                                : "Fetch full text for all included papers"}>
                            <i className="bi bi-cloud-download me-1"></i>Fetch Full Text
                        </button>
                    </div>

                    {/* GenAI Rights — split-pill with status badge */}
                    <div className="d-flex align-items-center btn-group custom-btn-group-outer">
                        {displayGenaiStatus === "RUNNING" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-yellow-fill-round ev-btn-small ev-radius-large">
                                Running
                            </span>
                        )}
                        {displayGenaiStatus === "COMPLETED" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-blue-fill-round ev-btn-small ev-radius-large">
                                Completed
                            </span>
                        )}
                        {displayGenaiStatus === "FAILED" && (
                            <span className="btn genbi-custom-btn-group-inner ev-btn-red-fill-round ev-btn-small ev-radius-large">
                                Failed
                            </span>
                        )}
                        <button
                            className="btn genbi-custom-btn-group-inner ev-btn-lightblue-fill-round ev-btn-small"
                            onClick={handleFetchGenAIRights}
                            disabled={isFetchingGR || !configFabricId}
                            title={selectedPaperIds.size
                                ? `Check GenAI rights for ${selectedPaperIds.size} selected papers`
                                : "Check GenAI rights for all included papers"}>
                            <i className="bi bi-shield-check me-1"></i>GenAI Rights
                        </button>
                    </div>

                    {/* Upload — functional */}
                    <input
                        type="file" accept=".pdf" multiple style={{ display: "none" }}
                        ref={fileInputRef}
                        onChange={e => handleFileChange(e.target.files)}
                    />
                    <button
                        className="ev-btn-blue-outline-round ev-btn-small"
                        disabled={isUploading}
                        onClick={() => fileInputRef.current?.click()}
                        title="Upload PDFs from your system"
                    >
                        {isUploading
                            ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Uploading…</>
                            : <><i className="bi bi-upload me-1"></i>Upload</>
                        }
                    </button>

                </div>
                )}

                {/* Tab 2 action buttons — in same row as tabs */}
                {activeTab === "fulltext" && (
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <button className="ev-btn-blue-fill-round ev-btn-small"
                        onClick={handleDownloadSelected}
                        disabled={selectedDocIds.size === 0}>
                        <i className="bi bi-download me-1"></i>Download {selectedDocIds.size > 0 ? `(${selectedDocIds.size})` : ""}
                    </button>
                    <button
                      className="ev-btn-lightblue-fill-round ev-btn-reg"
                      onClick={() => fetchDocuments(p2Page, p2Size, p2SearchQ)} disabled={p2Loading}
                    >
                      <i className="bi bi-arrow-repeat"></i> Refresh
                    </button>
                </div>
                )}
            </div>

            {/* ══════════════════════════════════════════
                TAB 1 — INCLUDED PAPERS
            ══════════════════════════════════════════ */}
            {activeTab === "included" && (
                <div className="d-flex flex-column flex-grow-1 overflow-hidden">

                    {/* search + refresh */}
                    <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                        <div className="position-relative w-55">
                            <input type="text" className="form-control search-input ms-0"
                                placeholder="Search by Study Id, Title or Author…"
                                value={p1Search}
                                onChange={e => setP1Search(e.target.value)}
                                onKeyPress={e => { if (e.key === "Enter") { setP1SearchQ(p1Search.trim()); setP1Page(1); } }}
                            />
                            <button className="ev-btn-lightblue-fill-round search-bar-button"
                                onClick={() => { setP1SearchQ(p1Search.trim()); setP1Page(1); }}>
                                <i className="bi bi-search"></i>
                            </button>
                        </div>
                        <div className="d-flex gap-2 align-items-center flex-shrink-0">
                            {selectedPaperIds.size > 0 && (
                                <span className="ev-xsmall-p-dark ev-bold-med">
                                    {selectedPaperIds.size} selected
                                </span>
                            )}
                            {bibliographicIds.size > 0 && (
                              <button
                                className={`ev-btn-light-fill ev-btn-reg ${p1Bibliographic ? "active" : ""}`}
                                onClick={() => { setP1Bibliographic(v => !v); setP1Page(1); }}
                                title="Show only bibliographic papers (added via Full-Text QC → Add Paper)"
                              >
                                <i className="bi bi-journal-text pe-1"></i>Bibliographic
                              </button>
                            )}
                            <button
                              className="ev-btn-lightblue-fill-round ev-btn-reg"
                              onClick={() => fetchIncluded(p1Page, p1Size, p1SearchQ, configFabricId)} disabled={p1Loading}>
                              <i className="bi bi-arrow-repeat"></i> Refresh
                            </button>
                            <button
                                className="ev-btn-blue-outline-round ev-btn-small"
                                onClick={handleDownloadList}
                                disabled={!configFabricId}
                                title={selectedPaperIds.size
                                    ? `Download list for ${selectedPaperIds.size} selected papers`
                                    : "Download list of all included papers"}
                            >
                                <i className="bi bi-download me-1"></i>Download List
                            </button>
                            {/* Bulk Purchase PDF */}
                            {bulkPdfEligible > 0 && (
                                <button
                                    className="ev-btn-blue-fill-round ev-btn-small"
                                    onClick={() => handleBulkPurchase("PDF")}
                                    title={`Purchase full text for ${bulkPdfEligible} paper(s)`}
                                >
                                    
                                    Purchase PDF ({bulkPdfEligible})
                                    {bulkPdfTotal > 0 && ` — $${bulkPdfTotal.toFixed(2)}`}
                                </button>
                            )}
                            {/* Bulk Purchase GenAI */}
                            {bulkGenaiEligible > 0 && (
                                <button
                                    className="ev-btn-lightblue-fill-round ev-btn-small"
                                    onClick={() => handleBulkPurchase("GENAI")}
                                    title={`Purchase GenAI rights for ${bulkGenaiEligible} paper(s)`}
                                >
                                  
                                    Purchase GenAI ({bulkGenaiEligible})
                                    {bulkGenaiTotal > 0 && ` — $${bulkGenaiTotal.toFixed(2)}`}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* select-all row */}
                    <div className="d-flex align-items-center gap-2 px-1 mb-1">
                        <input type="checkbox" className="form-check-input mt-0"
                            checked={isAllP1Selected} onChange={toggleAllP1} />
                        <span className="ev-xsmall-p-dark">Select all</span>
                    </div>

                    {/* column headers aligned with card columns */}
                    <div className="ev-card-8 ev-radius-small px-3 py-2 mb-1">
                        <div className="row align-items-center gx-2">
                            <div className="col text-center"><span className="ev-reg-p-dark ev-bold-med">DETAILS</span></div>
                            {/* FULL-TEXT / PDF header + filter */}
                            <div className="col-2 text-center position-relative" ref={ftFilterRef}>
                                <button
                                    className="border-0 bg-transparent d-flex align-items-center justify-content-center gap-1 w-100 ev-reg-p-dark ev-bold-med"
                                    onClick={() => { setFtDropOpen(o => !o); setGenaiDropOpen(false); }}
                                >
                                    FULL-TEXT / PDF
                                    <i className={`bi bi-funnel-fill ev-fs-11 ev-small-p-dark${p1FtFilter !== "ALL" ? " text-primary" : ""}`}></i>
                                </button>
                                {ftDropOpen && (
                                    <div className="position-absolute ev-card-4 ev-radius-small discovery-filter-dropdown p-1"
                                        style={{ top: "calc(100% + 4px)", right: 0, zIndex: 10 }}>
                                        {["ALL","OPEN_ACCESS","UNPURCHASED","ORDER_PLACED","PURCHASED"].map(v => {
                                            const label = v === "ALL" ? "All" : v === "OPEN_ACCESS" ? "Open Access" : v === "UNPURCHASED" ? "Unpurchased" : v === "ORDER_PLACED" ? "Order Placed" : "Purchased";
                                            return (
                                                <div key={v}
                                                    className={`discovery-filter-option ev-radius-small px-3 py-2 ev-small-p-dark ${p1FtFilter === v ? "ev-btn-blue-fill-round ev-bold-med" : ""}`}
                                                    onClick={() => { setP1FtFilter(v); setFtDropOpen(false); }}
                                                >{label}</div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            {/* GENAI RIGHTS header + filter */}
                            <div className="col-2 text-center position-relative" ref={genaiFilterRef}>
                                <button
                                    className="border-0 bg-transparent d-flex align-items-center justify-content-center gap-1 w-100 ev-reg-p-dark ev-bold-med"
                                    onClick={() => { setGenaiDropOpen(o => !o); setFtDropOpen(false); }}
                                >
                                    GENAI RIGHTS
                                    <i className={`bi bi-funnel-fill ev-fs-11 ev-small-p-dark${p1GenaiFilter !== "ALL" ? " text-primary" : ""}`}></i>
                                </button>
                                {genaiDropOpen && (
                                    <div className="position-absolute ev-card-4 ev-radius-small discovery-filter-dropdown p-1"
                                        style={{ top: "calc(100% + 4px)", right: 0, zIndex: 10 }}>
                                        {["ALL","OPEN_ACCESS","UNPURCHASED","ORDER_PLACED","PURCHASED"].map(v => {
                                            const label = v === "ALL" ? "All" : v === "OPEN_ACCESS" ? "Open Access" : v === "UNPURCHASED" ? "Unpurchased" : v === "ORDER_PLACED" ? "Order Placed" : "Purchased";
                                            return (
                                                <div key={v}
                                                    className={`discovery-filter-option ev-radius-small px-3 py-2 ev-small-p-dark ${p1GenaiFilter === v ? "ev-btn-blue-fill-round ev-bold-med" : ""}`}
                                                    onClick={() => { setP1GenaiFilter(v); setGenaiDropOpen(false); }}
                                                >{label}</div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* cards */}
                    <div className="scrollable-element flex-grow-1 pe-1">
                        {filteredPapers.length === 0 ? (
                            <div className="text-center p-5">
                                <i className="bi bi-journal-x ev-fs-48 text-muted d-block mb-2"></i>
                                <p className="ev-small-p-dark mb-0">No papers match the selected filters.</p>
                            </div>
                        ) : filteredPapers.map((paper) => {
                            const rowId = paper.study_id ?? paper.phase1_id;
                            const isSelected = selectedPaperIds.has(rowId);
                            return (
                                <div key={paper.phase1_id}
                                    className={`ev-card-8 w-100 ev-radius-small px-3 py-2 mb-2 cursor-pointer transition-all ${isSelected ? "active" : ""}`}
                                    onClick={() => togglePaper(rowId)}
                                >
                                    <div className="row gx-2 h-100">
                                        {/* Col 1 — card details */}
                                        <div className="col">
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <div onClick={e => e.stopPropagation()}>
                                                    <input type="checkbox" className="form-check-input mt-0"
                                                        checked={isSelected}
                                                        onChange={() => togglePaper(rowId)} />
                                                </div>
                                                <span className="ev-card-2 px-3 py-1 ev-radius-xsmall ev-small-p-dark ev-btn-xsmall ev-bold-med">
                                                    ID. {paper.id ?? paper.study_id ?? "—"}
                                                </span>
                                                {paper.year && (
                                                    <span className="ev-card-2 px-3 py-1 ev-radius-xsmall ev-xsmall-p-dark ev-btn-xsmall ev-bold-med">
                                                        Year. {paper.year}
                                                    </span>
                                                )}
                                                {bibliographicIds.has(String(paper.study_id ?? paper.id)) && (
                                                    <span className="ev-message-card3 px-3 py-1 ev-radius-xsmall ev-btn-xsmall ev-bold-med text-nowrap">
                                                        <i className="bi bi-journal-text pe-1"></i>Bibliographic
                                                    </span>
                                                )}
                                            </div>
                                            <p className="ev-bold-med ev-reg-p-dark mb-0">
                                                {paper.title || "—"}
                                            </p>
                                            {paper.authors && (
                                                <p className="ev-xsmall-p-dark mb-0 ev-bold-small">
                                                    <i>Authors. {paper.authors}</i>
                                                </p>
                                            )}
                                        </div>
                                        {/* Col 2 — Full-Text badge */}
                                        <div className="col-2 d-flex align-items-center justify-content-center">
                                            {renderAccessBadge(paper.full_text_status, {
                                                onPurchase: () => handleBulkPurchase("PDF"),
                                                canPurchase: true,
                                                price: paper.delivery_price,
                                            })}
                                        </div>
                                        {/* Col 3 — GenAI Rights badge */}
                                        <div className="col-2 d-flex align-items-center justify-content-center">
                                            {renderAccessBadge(paper.genai_status, {
                                                onPurchase: () => handleBulkPurchase("GENAI"),
                                                canPurchase: true,
                                                price: paper.genai_price,
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="ev-xsmall-p-dark">Showing {filteredPapers.length} of {paperMeta.total} papers</span>
                        <div className="d-flex align-items-center gap-2">
                            <button className="ev-btn-light-fill ev-btn-xsmall" disabled={p1Page === 1 || p1Loading}
                                onClick={() => setP1Page(p => p - 1)}><i className="bi bi-chevron-left"></i></button>
                            <span className="ev-xsmall-p-dark">Page {p1Page} of {p1TotalPages || 1}</span>
                            <button className="ev-btn-light-fill ev-btn-xsmall" disabled={p1Page >= p1TotalPages || p1Loading}
                                onClick={() => setP1Page(p => p + 1)}><i className="bi bi-chevron-right"></i></button>
                        </div>
                        <PaginationControls initialSize={p1Size} onSizeChange={s => { setP1Size(s); setP1Page(1); }} />
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════
                TAB 2 — FULL TEXT PAPERS
            ══════════════════════════════════════════ */}
            {activeTab === "fulltext" && (
                <div className="d-flex flex-column flex-grow-1 overflow-hidden">

                    {/* search */}
                    <div className="d-flex mb-2">
                        <div className="position-relative w-55">
                            <input type="text" className="form-control search-input ms-0"
                                placeholder="Search documents…" value={p2Search}
                                onChange={e => setP2Search(e.target.value)}
                                onKeyPress={e => { if (e.key === "Enter") { setP2SearchQ(p2Search.trim()); setP2Page(1); } }}
                            />
                            <button className="ev-btn-lightblue-fill-round search-bar-button"
                                onClick={() => { setP2SearchQ(p2Search.trim()); setP2Page(1); }}>
                                <i className="bi bi-search"></i>
                            </button>
                        </div>
                    </div>

                    {/* Select All row */}
                    <div className="d-flex align-items-center gap-2 px-2 py-1 mb-1">
                        <input type="checkbox" className="form-check-input mt-0"
                            checked={isAllP2Selected} onChange={toggleAllP2} />
                        <span className="ev-xsmall-p-dark ev-bold-med">Select All</span>
                    </div>

                    {/* rows */}
                    <div className="scrollable-element flex-grow-1 pe-1">
                        {documents.length === 0 ? (
                            <div className="text-center p-5">
                                <i className="bi bi-file-earmark-pdf ev-fs-48 text-muted d-block mb-2"></i>
                                <p className="ev-small-p-dark mb-0">No full-text documents yet. Use Upload above to add PDFs.</p>
                            </div>
                        ) : documents.map(doc => (
                            <div key={doc.doc_id}
                                className={`ev-radius-small px-2 py-2 mb-1 d-flex align-items-center gap-2 ${selectedDocIds.has(doc.doc_id) ? "ev-card-2" : "ev-card-2"}`}>
                                <input type="checkbox" className="form-check-input mt-0 flex-shrink-0"
                                    checked={selectedDocIds.has(doc.doc_id)} onChange={() => toggleDoc(doc.doc_id)} />
                                <i className="bi bi-file-earmark-pdf-fill text-danger flex-shrink-0"></i>
                                <p className="ev-reg-p-dark ev-bold-reg mb-0 flex-grow-1" style={{ overflowWrap: "anywhere" }}>
                                    {doc.document_name}
                                </p>
                                <button className="ev-btn-lightblue-fill-round ev-btn-small flex-shrink-0"
                                    onClick={() => openPreview(doc)}>
                                    <i className="bi bi-eye me-1"></i>Preview
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="ev-xsmall-p-dark">Showing {documents.length} of {counts.total} documents</span>
                        <div className="d-flex align-items-center gap-2">
                            <button className="ev-btn-light-fill ev-btn-xsmall" disabled={p2Page === 1 || p2Loading}
                                onClick={() => setP2Page(p => p - 1)}><i className="bi bi-chevron-left"></i></button>
                            <span className="ev-xsmall-p-dark">Page {p2Page} of {p2TotalPages || 1}</span>
                            <button className="ev-btn-light-fill ev-btn-xsmall" disabled={p2Page >= p2TotalPages || p2Loading}
                                onClick={() => setP2Page(p => p + 1)}><i className="bi bi-chevron-right"></i></button>
                        </div>
                        <PaginationControls initialSize={p2Size} onSizeChange={s => { setP2Size(s); setP2Page(1); }} />
                    </div>
                </div>
            )}

            <DocumentPreview
                isOpen={previewOpen}
                onClose={() => { setPreviewOpen(false); setPreviewDocId(null); setPreviewDocName(""); }}
                docId={previewDocId} documentName={previewDocName} bucket_type="input"
            />

            {/* ── Single purchase confirmation ── */}
            <ConfirmationModal
                show={purchaseModal.open}
                onClose={() => { setPurchaseModal({ open: false, paper: null, orderType: "PDF" }); setPurchaseConfirmText(""); }}
                onConfirm={handlePurchaseConfirm}
                title={purchaseModal.orderType === "GENAI" ? "Purchase GenAI Rights" : "Place Galaxy Order"}
                message={
                    singlePurchasePrice != null
                        ? `This will place a paid order for ${purchaseModal.orderType === "GENAI" ? "GenAI rights" : "the full text PDF"} at $${Number(singlePurchasePrice).toFixed(2)}. Do you want to proceed?`
                        : `This will place an order for ${purchaseModal.orderType === "GENAI" ? "GenAI rights" : "the full text PDF"}. Do you want to proceed?`
                }
                confirmButtonText="Place Order"
                confirmButtonClass="btn-primary"
                bodyContent={
                    <div>
                        <label className="ev-reg-p-dark mb-1 d-block">
                            Type <b>purchase</b> to confirm
                        </label>
                        <input
                            type="text"
                            className="formField6 w-100"
                            value={purchaseConfirmText}
                            onChange={e => setPurchaseConfirmText(e.target.value)}
                            onPaste={e => e.preventDefault()}
                            placeholder="purchase"
                            autoComplete="off"
                        />
                    </div>
                }
            />

            {/* ── Bulk purchase confirmation ── */}
            <ConfirmationModal
                show={bulkPurchaseModal.open}
                onClose={() => { setBulkPurchaseModal({ open: false, papers: [], orderType: "PDF" }); setBulkPurchaseConfirmText(""); }}
                onConfirm={handleBulkPurchaseConfirm}
                title={bulkPurchaseModal.orderType === "GENAI" ? "Bulk Purchase GenAI Rights" : "Bulk Purchase Full Text PDFs"}
                message={
                    `This will place orders for ${bulkPurchaseModal.papers.length} paper(s)` +
                    (bulkPurchaseTotal > 0 ? ` totalling $${bulkPurchaseTotal.toFixed(2)}` : "") +
                    `. Do you want to proceed?`
                }
                confirmButtonText="Place Orders"
                confirmButtonClass="btn-primary"
                bodyContent={
                    <div>
                        <label className="ev-reg-p-dark mb-1 d-block">
                            Type <b>purchase</b> to confirm
                        </label>
                        <input
                            type="text"
                            className="formField6 w-100"
                            value={bulkPurchaseConfirmText}
                            onChange={e => setBulkPurchaseConfirmText(e.target.value)}
                            onPaste={e => e.preventDefault()}
                            placeholder="purchase"
                            autoComplete="off"
                        />
                    </div>
                }
            />

            {isProcessingPopupVisible && <ProcessingPopup message={processingMessage || "Processing…"} />}
            {isErrorPopupVisible && <ErrorPopup message={popupErrorMessage} onClose={() => setIsErrorPopupVisible(false)} />}
            {isSuccessPopupVisible && <SuccessPopup message={popupSuccessMessage} onClose={() => setIsSuccessPopupVisible(false)} />}
        </div>
    );
};

export default FullTextList;